import React, { useState } from 'react';
import { Copy, Check, Users, TrendingUp, DollarSign, Share2, UserPlus, Award, Wallet } from 'lucide-react';
import { Container } from '../components/layout/Container';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Spinner } from '../components/common/Spinner';
import { useAuth } from '../context/AuthContext';
import { useAffiliateMetrics } from '../hooks/useAffiliate';
import { useWallet } from '../hooks/useWallet';
import { formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';

export const Affiliate: React.FC = () => {
  const { user } = useAuth();
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const [copied, setCopied] = useState(false);
  
  const { data, isLoading: metricsLoading } = useAffiliateMetrics({ month: currentMonth, year: currentYear });
  const { data: walletData, isLoading: walletLoading } = useWallet();
  const metrics = data?.data;
  const currency = walletData?.data?.currency || 'USD';

  const affiliateLink = metrics?.affiliateLink || `${window.location.origin}/register?ref=${user?.affiliateCode}`;

  const isLoading = metricsLoading || walletLoading;

  const handleCopy = () => {
    navigator.clipboard.writeText(affiliateLink);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const steps = [
    { icon: Share2, title: 'Share Your Link', desc: 'Share your unique affiliate link with friends, family, or on social media.' },
    { icon: UserPlus, title: 'They Sign Up', desc: 'When someone registers using your link, they become your referral.' },
    { icon: Award, title: 'Earn Commissions', desc: 'You earn a percentage of their ticket purchases for a limited time period.' },
    { icon: Wallet, title: 'Get Paid', desc: 'Commissions are automatically added to your wallet and can be withdrawn anytime.' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <Container className="py-12"><Spinner size="lg" /></Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      <Container className="py-10">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Affiliate Program</h1>

        {/* Affiliate Link */}
        <Card className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Share2 className="h-4 w-4 text-indigo-600" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Your Affiliate Link</h2>
          </div>
          <div className="flex gap-2">
            <Input value={affiliateLink} readOnly className="flex-1" />
            <Button onClick={handleCopy}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            Share this link with friends and earn commissions on their ticket purchases
          </p>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-indigo-50 dark:bg-indigo-950 rounded-full -mr-6 -mt-6" />
            <div className="relative">
              <Users className="h-5 w-5 text-indigo-600 mb-2" />
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Referrals</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-0.5">{metrics?.totalReferrals || 0}</p>
            </div>
          </Card>
          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-50 dark:bg-emerald-950 rounded-full -mr-6 -mt-6" />
            <div className="relative">
              <TrendingUp className="h-5 w-5 text-emerald-600 mb-2" />
              <p className="text-xs text-gray-500 dark:text-gray-400">This Month Commissions</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-0.5">
                {metrics?.metrics?.find(m => m.month === currentMonth && m.year === currentYear)?.referrals || 0}
              </p>
            </div>
          </Card>
          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-purple-50 dark:bg-purple-950 rounded-full -mr-6 -mt-6" />
            <div className="relative">
              <DollarSign className="h-5 w-5 text-purple-600 mb-2" />
              <p className="text-xs text-gray-500 dark:text-gray-400">This Month Earnings</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-0.5">
                {formatCurrency(metrics?.metrics?.find(m => m.month === currentMonth && m.year === currentYear)?.earnings || 0, currency)}
              </p>
            </div>
          </Card>
        </div>

        {/* How It Works */}
        <Card>
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-5">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <div className="bg-indigo-100 dark:bg-indigo-900 text-indigo-600 rounded-xl w-9 h-9 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{step.title}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </Container>
    </div>
  );
};
